import { useRef, useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import DrawToolbar from './DrawToolbar'

interface FotoAnnotateProps {
  imageUrl: string
  onConfirm: (blob: Blob) => void
  onCancel: () => void
}

interface Point {
  x: number
  y: number
}

const COLORS = ['#D93025', '#C4D44A', '#ffffff', '#111111', '#C8A84B']
const MAX_HISTORY = 20

/**
 * FotoAnnotate — desenha sobre uma foto existente.
 * Retorna blob da imagem anotada via onConfirm.
 */
export default function FotoAnnotate({ imageUrl, onConfirm, onCancel }: FotoAnnotateProps) {
  const canvasRef  = useRef<HTMLCanvasElement>(null)
  const drawRef    = useRef(false)
  const lastPos    = useRef<Point | null>(null)
  const historyRef = useRef<ImageData[]>([])
  const imgRef     = useRef<HTMLImageElement | null>(null)
  const [tool,      setTool]      = useState<'pen' | 'erase'>('pen')
  const [color,     setColor]     = useState('#D93025')
  const [thickness, setThickness] = useState(4)
  const [canUndo,   setCanUndo]   = useState(false)
  const [loaded,    setLoaded]    = useState(false)

  // Carrega imagem no canvas
  useEffect(() => {
    if (!imageUrl) return
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imgRef.current = img
      const canvas = canvasRef.current
      if (!canvas) return
      requestAnimationFrame(() => {
        const containerW = canvas.offsetWidth  || window.innerWidth
        const containerH = canvas.offsetHeight || (window.innerHeight - 120)
        // Escalar proporcionalmente
        const scale = Math.min(containerW / img.width, containerH / img.height, 1)
        canvas.width  = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        historyRef.current = []
        setCanUndo(false)
        setLoaded(true)
      })
    }
    img.onerror = () => {
      toast.error('Erro ao carregar imagem')
      onCancel()
    }
    img.src = imageUrl
  }, [imageUrl, onCancel])

  function getPos(e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement): Point {
    const rect = canvas.getBoundingClientRect()
    const src  = 'touches' in e && e.touches[0] ? e.touches[0] : (e as React.MouseEvent).nativeEvent
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    }
  }

  function saveHistory() {
    const canvas = canvasRef.current
    if (!canvas || !canvas.width) return
    const ctx = canvas.getContext('2d')
    const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height)
    historyRef.current = [...historyRef.current.slice(-MAX_HISTORY + 1), snapshot]
    setCanUndo(true)
  }

  function startDraw(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault()
    saveHistory()
    drawRef.current = true
    lastPos.current = getPos(e, canvasRef.current)
  }

  function draw(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault()
    if (!drawRef.current) return
    const canvas = canvasRef.current
    const ctx    = canvas.getContext('2d')
    const pos    = getPos(e, canvas)

    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = tool === 'erase' ? 'rgba(0,0,0,0)' : color
    ctx.lineWidth   = tool === 'erase' ? thickness * 6 : thickness
    ctx.lineCap     = 'round'
    ctx.lineJoin    = 'round'
    if (tool === 'erase') {
      ctx.globalCompositeOperation = 'destination-out'
    } else {
      ctx.globalCompositeOperation = 'source-over'
    }
    ctx.stroke()
    ctx.globalCompositeOperation = 'source-over'
    lastPos.current = pos
  }

  function endDraw() { drawRef.current = false }

  const undo = useCallback(() => {
    if (!historyRef.current.length) return
    const canvas = canvasRef.current
    const ctx    = canvas.getContext('2d')
    const prev   = historyRef.current.pop()
    ctx.putImageData(prev, 0, 0)
    setCanUndo(historyRef.current.length > 0)
  }, [])

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.toBlob(blob => {
      if (blob) onConfirm(blob)
      else onCancel()
    }, 'image/jpeg', 0.92)
  }, [onConfirm, onCancel])

  return (
    <div className="fixed inset-0 z-[320] flex flex-col" style={{ background: '#0a0a0a' }}>
      <DrawToolbar
        tool={tool} setTool={setTool}
        color={color} setColor={setColor}
        thickness={thickness} setThickness={setThickness}
        colors={COLORS}
      />

      {/* Canvas com imagem */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {!loaded && <span className="text-[13px] text-[#555]">Carregando...</span>}
        <canvas
          ref={canvasRef}
          className="touch-none max-w-full max-h-full"
          style={{ cursor: tool === 'erase' ? 'cell' : 'crosshair', display: loaded ? 'block' : 'none' }}
          onMouseDown={startDraw}  onMouseMove={draw}  onMouseUp={endDraw}  onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw}  onTouchEnd={endDraw}
        />
      </div>

      {/* Bottom bar */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 bg-[#1a1a1a] border-t border-white/[0.08]"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={undo}
          disabled={!canUndo}
          className="flex items-center gap-1 px-3 py-2 text-[12px] rounded-lg border-none bg-transparent cursor-pointer disabled:opacity-30"
          style={{ color: canUndo ? '#ccc' : '#666' }}
        >
          <span className="material-symbols-outlined text-base">undo</span>
          Desfazer
        </button>

        <div className="flex-1" />

        <button
          onClick={onCancel}
          className="px-4 py-2 text-[13px] text-[#888] rounded-xl border-none bg-transparent cursor-pointer hover:text-[#f0f0f0]"
        >
          Cancelar
        </button>
        <button
          onClick={handleConfirm}
          className="px-5 py-2.5 text-[13px] font-semibold rounded-full border-none cursor-pointer"
          style={{ background: 'var(--color-hero)', color: '#111' }}
        >
          Salvar
        </button>
      </div>
    </div>
  )
}
